/**
 * Bloomyer Team — Centre-focus carousel
 * Custom element: <bte-team>
 *
 * Behaviour:
 * - Active card is always visually centred in the viewport
 * - Adjacent cards scale down (CSS: is-adjacent, default)
 * - Drag / swipe to navigate; click a card to focus it
 * - Dots navigate to the corresponding card
 * - Play button opens a YouTube / Vimeo video in a modal overlay
 */

if (!customElements.get('bte-team')) {
  class BteTeam extends HTMLElement {
    constructor() {
      super();
      this.track    = null;
      this.viewport = null;
      this.cards    = [];
      this.dots     = [];
      this.modal    = null;
      this.iframe   = null;

      this.currentIndex = 0;

      // Drag state
      this.isDragging       = false;
      this.startX           = 0;
      this.currentTranslate = 0;
      this.prevTranslate    = 0;
    }

    /* ─────────────────────────────────────────
       Lifecycle
    ───────────────────────────────────────── */
    connectedCallback() {
      this.track    = this.querySelector('.bte-track');
      this.viewport = this.querySelector('.bte-viewport');
      this.cards    = [...this.querySelectorAll('.bte-card')];
      this.dots     = [...this.querySelectorAll('.bte-dot')];
      this.modal    = this.querySelector('.bte-modal');
      this.iframe   = this.querySelector('.bte-modal__iframe');
      this.video    = this.querySelector('.bte-modal__video');

      if (!this.track || this.cards.length === 0) return;

      // Dots → navigate
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.slideTo(i));
      });

      // Play buttons → open modal
      this.querySelectorAll('.bte-card__play').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const mp4 = btn.dataset.videoMp4;
          const url = btn.dataset.video;
          if (mp4)      this.openNativeVideo(mp4);
          else if (url) this.openModal(url);
        });
      });

      // Click on a card → focus it
      this.cards.forEach((card, i) => {
        card.addEventListener('click', () => {
          if (!this._dragged) this.slideTo(i);
        });
      });

      // Modal close
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.closeModal();
        });
        const closeBtn = this.modal.querySelector('.bte-modal__close');
        if (closeBtn) closeBtn.addEventListener('click', () => this.closeModal());
      }

      // Keyboard: Escape closes modal, arrows navigate
      this._onKeydown = (e) => {
        if (e.key === 'Escape') this.closeModal();
        if (e.key === 'ArrowRight') this.slideTo(this.currentIndex + 1);
        if (e.key === 'ArrowLeft')  this.slideTo(this.currentIndex - 1);
      };
      document.addEventListener('keydown', this._onKeydown);

      this.initDrag();
      this.initMobileScroll();

      // Mobile : 1ère carte ; desktop : carte centrale (Figma)
      const start = window.innerWidth <= 749 ? 0 : Math.floor((this.cards.length - 1) / 2);
      this.slideTo(start, false);

      // Re-centrer au resize (desktop uniquement)
      this._onResize = () => { if (window.innerWidth > 749) this.slideTo(this.currentIndex, false); };
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKeydown);
    }

    /* ─────────────────────────────────────────
       Geometry helpers
    ───────────────────────────────────────── */
    getVar(name) {
      const v = parseFloat(getComputedStyle(this).getPropertyValue(name));
      return isNaN(v) ? 0 : v;
    }

    /**
     * Offset so that the card at `index` is horizontally centred.
     * All cards except the active one use the base width; the active card
     * (the one at `index`) uses the larger active width. Reading the widths
     * from CSS custom properties keeps the maths correct and responsive,
     * regardless of the in-flight width transition.
     *
     * left(index)   = index × (baseW + gap)
     * center(index) = left + activeW / 2
     * offset        = viewportW / 2 − center
     */
    getOffset(index) {
      const gap     = this.getVar('--bte-gap');
      const baseW   = this.getVar('--bte-card-w');
      const activeW = this.getVar('--bte-card-active-w');
      const viewW   = this.viewport.offsetWidth;
      const left    = index * (baseW + gap);
      const center  = left + activeW / 2;
      return viewW / 2 - center;
    }

    /* ─────────────────────────────────────────
       Navigation
    ───────────────────────────────────────── */
    slideTo(index, animate = true) {
      const max = this.cards.length - 1;
      this.currentIndex = Math.max(0, Math.min(index, max));

      // Mobile : scroll natif scroll-snap
      if (window.innerWidth <= 749) {
        const card = this.cards[this.currentIndex];
        if (card) {
          const scrollTarget = card.offsetLeft - (this.viewport.offsetWidth - card.offsetWidth) / 2;
          this.viewport.scrollTo({ left: Math.max(0, scrollTarget), behavior: animate ? 'smooth' : 'auto' });
        }
        this.updateCardStates();
        this.updateDots();
        return;
      }

      const offset = this.getOffset(this.currentIndex);
      this.currentTranslate = offset;
      this.prevTranslate    = offset;

      if (animate) {
        this.track.classList.remove('no-transition');
      } else {
        this.track.classList.add('no-transition');
        // Force a reflow so the class takes effect before transform
        void this.track.offsetHeight;
      }

      this.track.style.transform = `translateX(${offset}px)`;

      this.updateCardStates();
      this.updateDots();
    }

    /* ─────────────────────────────────────────
       Card & dot states
    ───────────────────────────────────────── */
    updateCardStates() {
      this.cards.forEach((card, i) => {
        card.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        const active = i === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    /* ─────────────────────────────────────────
       Video modal
    ───────────────────────────────────────── */
    openModal(rawUrl) {
      if (!this.modal || !this.iframe) return;

      let embedUrl = rawUrl;

      const ytMatch     = rawUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      const vimeoMatch  = rawUrl.match(/vimeo\.com\/(\d+)/);

      if (ytMatch) {
        embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
      } else if (vimeoMatch) {
        embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
      }

      // External embed → iframe visible, native player hidden
      if (this.video) { this.video.pause(); this.video.removeAttribute('src'); this.video.style.display = 'none'; }
      this.iframe.style.display = '';
      this.iframe.src = embedUrl;
      this.openOverlay();
    }

    openNativeVideo(src) {
      if (!this.modal || !this.video) return;

      // Native Shopify video → <video> visible, iframe hidden
      if (this.iframe) { this.iframe.src = ''; this.iframe.style.display = 'none'; }
      this.video.style.display = '';
      this.video.src = src;
      this.openOverlay();
      const playPromise = this.video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});
    }

    openOverlay() {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      if (this.iframe) this.iframe.src = '';
      if (this.video)  { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); }
      document.body.style.overflow = '';
    }

    /* ─────────────────────────────────────────
       Drag / swipe
    ───────────────────────────────────────── */
    initDrag() {
      const vp = this.viewport;

      vp.addEventListener('mousedown',  (e) => this.dragStart(e));
      vp.addEventListener('mousemove',  (e) => this.dragMove(e));
      vp.addEventListener('mouseup',    ()  => this.dragEnd());
      vp.addEventListener('mouseleave', ()  => { if (this.isDragging) this.dragEnd(); });

      vp.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
      vp.addEventListener('touchmove',  (e) => this.dragMove(e),  { passive: false });
      vp.addEventListener('touchend',   ()  => this.dragEnd());

      // Prevent native image drag
      this.track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }

    /* ─────────────────────────────────────────
       Mobile scroll-snap — sync dots
    ───────────────────────────────────────── */
    initMobileScroll() {
      let rAF = null;
      this.viewport.addEventListener('scroll', () => {
        if (rAF) cancelAnimationFrame(rAF);
        rAF = requestAnimationFrame(() => this.updateActiveDotFromScroll());
      }, { passive: true });
    }

    updateActiveDotFromScroll() {
      if (window.innerWidth > 749) return;
      const vpCenter = this.viewport.scrollLeft + this.viewport.offsetWidth / 2;
      let closest = 0;
      let minDist  = Infinity;
      this.cards.forEach((card, i) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(vpCenter - cardCenter);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      if (closest !== this.currentIndex) {
        this.currentIndex = closest;
        this.updateCardStates();
        this.updateDots();
      }
    }

    getPositionX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    dragStart(e) {
      if (window.innerWidth <= 749) return;
      this.isDragging    = true;
      this._dragged      = false;
      this.startX        = this.getPositionX(e);
      this.prevTranslate = this.currentTranslate;

      this.track.classList.add('no-transition');
      this.viewport.classList.add('is-dragging');
    }

    dragMove(e) {
      if (window.innerWidth <= 749 || !this.isDragging) return;
      if (e.cancelable) e.preventDefault();

      const diff = this.getPositionX(e) - this.startX;

      // Mark as dragged once threshold crossed (avoids treating tiny drag as click)
      if (Math.abs(diff) > 8) this._dragged = true;

      this.currentTranslate = this.prevTranslate + diff;
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }

    dragEnd() {
      if (window.innerWidth <= 749 || !this.isDragging) return;
      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');

      const moved    = this.currentTranslate - this.prevTranslate;
      const threshold = 60;

      if (moved < -threshold) {
        this.slideTo(this.currentIndex + 1);
      } else if (moved > threshold) {
        this.slideTo(this.currentIndex - 1);
      } else {
        // Snap back to current
        this.slideTo(this.currentIndex);
      }

      // Brief delay before resetting _dragged so click handler reads the flag
      setTimeout(() => { this._dragged = false; }, 50);
    }
  }

  customElements.define('bte-team', BteTeam);
}
